/**
 * Context-window usage, as the gateway reports it.
 *
 * Two paths land on `parseSessionContextUsage`: the `sessions.describe` reply
 * (`{ session: <row> }`), used to seed on connect and session switch, and the
 * `sessions.changed` push, which flattens the same fields onto the payload.
 *
 * Only measured numbers appear here. Deliberately absent, because the gateway
 * has nothing honest to put in them:
 *
 *  - a category breakdown — openclaw has no per-section token accounting;
 *  - `estimatedCostUsd` — always 0, since the catalog this fleet generates
 *    writes `contextTokens` without a per-model `cost`;
 *  - per-call `inputTokens` / `outputTokens` — `input` is only the UNCACHED
 *    slice of a prompt (63 against a real 22,602), and the `cacheRead` /
 *    `cacheWrite` buckets that would reconcile it are not on the row.
 */

/** The subset of a gateway session row that describes context usage. */
export interface SessionContextUsage {
  /**
   * Prompt tokens the last run sent — how full the window was AS OF THAT RUN,
   * not a session total and not a live reading.
   *
   * `null` until the gateway has a figure, which is NOT after the first run:
   * openclaw leaves `totalTokens` at 0 for the life of the websocket
   * connection and only corrects it on a fresh connection or a second run. No
   * RPC forces a re-read, so the meter shows the window alone and marks the
   * numerator pending rather than disappearing.
   */
  readonly usedTokens: number | null;
  /** Size of the model's context window. */
  readonly limitTokens: number;
  /**
   * False when `usedTokens` is reconstructed from the stored transcript rather
   * than measured on a live run. The gateway's own UI renders stale counts
   * with a `~`; we do the same rather than hiding them, since a stale count is
   * still the right order of magnitude.
   */
  readonly fresh: boolean;
}

function readNumber(
  source: Record<string, unknown>,
  key: string,
): number | null {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return value;
}

/**
 * Merge a fresh reading into the stored one, returning `prev` BY IDENTITY when
 * the incoming row says nothing new — callers rely on that to skip a re-render.
 *
 * Keeps the existing fill when the row carries the window but no numerator.
 * Every `sessions.changed` ships the whole session snapshot, so a rename, a
 * pin, or a `phase: "error"` push would otherwise overwrite `22.5k / 128k` with
 * `— / 128k`. A changed WINDOW is taken instead: a different model is in play
 * and the old fill describes nothing. A fill that goes DOWN is taken too — a
 * compaction or a tool-result trim.
 *
 * An in-place reset does not reach the keep-fill branch. Driven against a live
 * openclaw (2026.7.1), `/clear` leaves `totalTokens` at the last run's value
 * and the next run rewrites it with the new, smaller prompt size:
 *
 *     before   { contextTokens: 128000, totalTokens: 7774 }
 *     after    { contextTokens: 128000, totalTokens: 7797 }
 *     next run { contextTokens: 128000, totalTokens: 7766 }
 *
 * `/compact` was not driven; the decreasing-fill case covers it by inference.
 */
export function reconcileSessionContextUsage(
  prev: SessionContextUsage | null,
  next: SessionContextUsage | null,
): SessionContextUsage | null {
  if (next == null) return prev;
  if (prev == null) return next;

  const keepFill =
    next.usedTokens == null && prev.limitTokens === next.limitTokens;
  // Returned as `prev` rather than a merged copy: substituting the previous
  // numerator into `next` reproduces `prev` field for field anyway.
  if (keepFill) return prev;

  return next.usedTokens === prev.usedTokens &&
    next.limitTokens === prev.limitTokens &&
    next.fresh === prev.fresh
    ? prev
    : next;
}

/**
 * Narrow a gateway session row — or a `sessions.changed` payload, which
 * carries the same fields inline — into `SessionContextUsage`.
 *
 * `null` only when there is no window size, since a meter with no denominator
 * can render nothing. A missing NUMERATOR is the normal first-turn state and
 * comes back as `usedTokens: null`. Callers treat `null` as "nothing to show"
 * and keep what they had, so a partial row never blanks a good reading.
 */
export function parseSessionContextUsage(
  row: unknown,
): SessionContextUsage | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const source = row as Record<string, unknown>;

  const limitTokens = readNumber(source, 'contextTokens');
  if (limitTokens == null || limitTokens <= 0) return null;

  // Zero means "not measured yet", not "empty" — reading it as a real value
  // renders a 0% meter under a visible conversation.
  const rawUsed = readNumber(source, 'totalTokens');
  const usedTokens = rawUsed == null || rawUsed === 0 ? null : rawUsed;

  return {
    usedTokens,
    limitTokens,
    fresh: source.totalTokensFresh === true,
  };
}
